import { useCallback, useEffect, useState } from "react";

import { useTranslate } from "helpers";
import { writeOffFleetTireSetAPI } from "services/api/fleet/tires/api";
import { FleetTireSetWriteOffFormValues } from "services/api/fleet/tires/types";
import { initialFleetTireSetWriteOffForm } from "services/api/fleet/tires/consts";
import { useHandleCheckboxChange } from "services/forms/hooks";

import Modal from "ui/components/Modal/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { CheckBox } from "ui/components/CheckBox";
import { Alert } from "ui/components/Alert";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import { TireSetWriteOffModalProps } from "./types";

const TireSetWriteOffModal: React.FC<TireSetWriteOffModalProps> = ({
  show,
  tireSetId,
  onSuccess,
  onHide,
}) => {
  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetTireSetWriteOffFormValues>(
    initialFleetTireSetWriteOffForm
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) setFormValues(initialFleetTireSetWriteOffForm);
  }, [show]);

  const handleCheckboxChange = useHandleCheckboxChange(setFormValues);

  const handleUploadSave = useCallback((newUploads: FleetDocumentUpload[]) => {
    setFormValues((old) => ({
      ...old,
      uploads: [...old.uploads, ...newUploads],
    }));
  }, []);

  const handleUploadDelete = useCallback((uploadId: string) => {
    setFormValues((old) => ({
      ...old,
      uploads: old.uploads.filter((u) => u.id !== uploadId),
    }));
  }, []);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      await writeOffFleetTireSetAPI(tireSetId, formValues, t);
      onSuccess();
    } catch {
      // handled by API interceptors
    } finally {
      setIsLoading(false);
    }
  }, [formValues, tireSetId, onSuccess, t]);

  const isSubmitDisabled = isLoading;

  return (
    <Modal show={show} position="centered" size="small" onHide={onHide}>
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title="admin.fleet.tires.sets.writeoff.title"
        onHide={onHide}
      />
      <ModalBody>
        <div className="px-4 pt-5 pb-8 space-y-5">
          <Alert
            variant="warning"
            message="Označavanjem seta guma kao otpisanog gume se trajno uklanjaju iz upotrebe. Priložite dokumentaciju o ekološkom zbrinjavanju."
          />

          <CheckBox
            name="ecologicallyDisposed"
            checked={formValues.ecologicallyDisposed}
            onChange={handleCheckboxChange}
            label={t("admin.fleet.tires.sets.writeoff.ecodisposed")}
          />

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("admin.fleet.tires.sets.writeoff.uploadlabel")}
            </div>
            <FileUploadControl
              uploads={formValues.uploads}
              onUploadSave={handleUploadSave}
              onUploadDelete={handleUploadDelete}
              buttonText="Dodaj dokumentaciju"
              modalTitle="Dokumentacija o zbrinjavanju"
              emptyText="Kliknite za dodavanje dokumentacije o zbrinjavanju"
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 flex justify-end gap-3">
          <Button variant="outlined" onClick={onHide} disabled={isLoading}>
            Odustani
          </Button>
          <Button
            variant="remove"
            onClick={handleConfirm}
            disabled={isSubmitDisabled}
          >
            {t("admin.fleet.tires.sets.writeoff.confirm")}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default TireSetWriteOffModal;
